import { domainToProfileIdOptionsMap } from '@/contentprofile/domain_content_profiles'

// Shared by the tags and the domain flags; the helpers derive every spelling from it.
export const GENERATE_ASSESSMENT_FEATURE = 'toolkit-pma-generation'

// Shared verbatim with threads-app's visibilityStore, which derives the same
// exclude_feature_threads_transition domain flag from it. The Transition Plan card here
// links straight into the Threads route that flag also gates, so the two must agree: a
// card Toolkit shows and Threads then bounces is worse than no card at all.
export const TRANSITION_THREAD_FEATURE = 'feature-threads-transition'

// The flag names set on a domain, whatever shape the graph sent them in. Toolkit's Ruby
// reads these with Hash#include? / Array#include? interchangeably (app.rb:275-283), so it
// cannot tell an array of names from a map of name => value, and nothing here establishes
// which arrives. threads-app's graph_service._convert_ruby_value handles both shapes from
// the same upstream and stringifies values, so a flag stored as :false arrives as the
// string "false" and must not read as set. Calling .some on an object would throw inside a
// getter and take the page down with it.
//
// threadsVisibility is normalised through this too, because generateAssessmentVisibility
// evaluates it BEFORE the include and exclude gates -- leaving it unnormalised would have
// meant this feature threw on a map before its own defences ran, which made the defences
// pointless. growVisibility, hb2Visibility and analyticsDashboardVisibility still read
// flags directly and would still throw on that input; they are not on this feature's path
// and are left for a separate change.
const TRUTHY_FLAG_VALUES = ['true', '1', 'yes']
const domainFlagNames = (flags) => {
  if (Array.isArray(flags)) return flags.map((flag) => String(flag).toLowerCase())
  if (flags && typeof flags === 'object') {
    return Object.keys(flags)
      .filter((key) => {
        const value = flags[key]
        if (typeof value === 'string') return TRUTHY_FLAG_VALUES.includes(value.toLowerCase())
        return value === true || value === 1
      })
      .map((key) => key.toLowerCase())
  }
  return []
}

// EVERY domain, not just the first. domainInfo (= domains[0]) is what growVisibility and
// hb2Visibility read, but those are opt-IN, where missing a later domain shows the user
// less -- the safe direction. These gates are opt-OUT, where missing a later domain shows
// MORE: a district that excluded itself would still see the feature because the graph
// happened to return another domain first. So both halves scan all of them, as isMultiyear
// and interactiveGuidesOn already do.
const anyDomainHasFlag = (domains, flagName) =>
  (domains || []).some((domain) => domainFlagNames(domain && domain.flags).includes(flagName))

const DISABLED_THREADS_DOMAIN_NAME_PATTERNS = []

export default {
  namespaced: true,
  state: {
    betaMode: false,
    editorMode: false,
    iodMode: null,
    analyticsDashboardMode: null,
    effectsMode: null,
    shareMode: null,
    threadsMode: null,
    orientationMobilityMode: null,
    growMode: null,
    hb2Mode: null,
    generateAssessmentMode: null,
    disabledThreadsDomains: DISABLED_THREADS_DOMAIN_NAME_PATTERNS,
  },
  mutations: {
    toggleBetaMode(state, flag) {
      state.betaMode = flag
    },
    toggleEditorMode(state, flag) {
      state.editorMode = flag
    },
    toggleIodMode(state, flag) {
      state.iodMode = flag
    },
    toggleAnalyticsDashboardMode(state, flag) {
      state.analyticsDashboardMode = flag
    },
    toggleEffectsMode(state, flag) {
      state.effectsMode = flag
    },
    toggleShareMode(state, flag) {
      state.shareMode = flag
    },
    toggleThreadsMode(state, flag) {
      state.threadsMode = flag
    },
    toggleOrientationMobilityMode(state, flag) {
      state.orientationMobilityMode = flag
    },
    toggleGrowMode(state, flag) {
      state.growMode = flag
    },
    toggleHb2Mode(state, flag) {
      state.hb2Mode = flag
    },
    toggleGenerateAssessmentMode(state, flag) {
      state.generateAssessmentMode = flag
    },
  },
  actions: {
    enableBetaMode({ commit }) {
      commit('toggleBetaMode', true);
    },
  },
  getters: {
    editorVisibility(state, getters, rootState, rootGetters) {
      return state.editorMode && rootGetters['user/isSuperAdmin']
    },
    betaVisibility(state, getters, rootState, rootGetters) {
      return state.betaMode && rootGetters['user/isAuthenticated']
    },
    teksVisibility(state, getters, rootState, rootGetters) {
      return rootGetters['user/isTeks'] || rootGetters['contentProfile/profileId'] == 'texas'
    },
    selVisibility(state, getters, rootState, rootGetters) {
      const profileId = rootGetters['contentProfile/profileId'] || ''
      return !profileId.endsWith('no_SEL')
    },
    iodVisibility(state, getters, rootState, rootGetters) {
      if (state.iodMode !== null) return state.iodMode
      // check if the content profile is one that doesn't allow IOD feature
      // no-iod-test profile is used for cypress testing
      const noIODProfiles = ['nevada-no-iod', 'no-iod-test']
      return noIODProfiles.indexOf(rootGetters['contentProfile/profileId']) == -1
    },
    analyticsDashboardVisibility(state, getters, rootState, rootGetters) {
      if (state.analyticsDashboardMode !== null) return state.analyticsDashboardMode
      const domainFlags = rootGetters['user/domainInfo']?.flags || []
      return domainFlags.includes('analytics_dashboard')
    },
    effectsVisibility(state, getters, rootState, rootGetters) {
      if (state.effectsMode !== null) return state.effectsMode
      // check if the content profile is one that has effect statements in PLW
      const effectsProfiles = ['nevada-no-iod']
      return effectsProfiles.indexOf(rootGetters['contentProfile/profileId']) > -1
    },
    isCCSDProfile(state, getters, rootState, rootGetters) {
      return rootGetters['contentProfile/profileId'] === 'nevada-no-iod'
    },
    shareVisibility(state, getters, rootState, rootGetters) {
      if (state.shareMode !== null) return state.shareMode
      // check if the content profile is one that doesn't allow share feature
      const profileId = rootGetters['contentProfile/profileId'] || ''
      // profiles where share is disabled
      const noShareProfiles = new Set(['florida_no_share_no_SEL'])
      return !noShareProfiles.has(profileId)
    },
    threadsVisibility(state, getters, rootState, rootGetters) {
      if (state.threadsMode !== null) return state.threadsMode
      const isAuthenticated = rootGetters['user/isAuthenticated']
      if (!isAuthenticated) return false

      const userDomainName = rootGetters['user/domainInfo']?.name || ''
      const belongsToDisabledDomain = state.disabledThreadsDomains.some(domain => userDomainName.includes(domain))

      // Normalised, unlike the other flag reads in this file, because this getter is on
      // generateAssessmentVisibility's path: it is evaluated before the include and
      // exclude gates, so a map-shaped flags object would throw here and take the feature
      // (and this page) down before their own normalisation ever ran.
      const hasNoThreadsFlag = domainFlagNames(rootGetters['user/domainInfo']?.flags).includes('no_threads')

      const profileId = rootGetters['contentProfile/profileId'] || ''
      const hasNoAIProfile = profileId.endsWith('no-ai')

      const hasAccessOverride = !!rootGetters['user/hasThreadsAccessOverrideTag']
      const blockedByDomain = (belongsToDisabledDomain || hasNoThreadsFlag) && !hasAccessOverride

      return !blockedByDomain && !hasNoAIProfile
    },
    // Transition Thread is released, so this is opt-OUT only: an exclude_ domain flag turns
    // the card off for a district, an exclude- user tag for one person, and the opt-in tag
    // (the bare feature-threads-transition key) keeps one user on inside an opted-out
    // district. No AI floor of its own — the card is already behind threadsVisibility in
    // Home.vue, which carries the no-ai and no_threads checks.
    //
    // No per-profile carve-out: CCSD is kept out by the exclude_ flag on its domain like any
    // other district, so early-access reviewers there get in by the opt-in tag, and releasing
    // to CCSD means removing that flag in Admin rather than a code change.
    transitionThreadVisibility(state, getters) {
      return !getters.isFeatureExcluded(TRANSITION_THREAD_FEATURE)
    },
    canChangeProfile(state, getters, rootState, rootGetters) {
      const userDomainUuid = rootGetters['user/userDomainUuid']
      return new Set(Object.keys(domainToProfileIdOptionsMap)).has(userDomainUuid)
    },
    orientationMobilityVisibility(state, getters, rootState, rootGetters) {
      if (state.orientationMobilityMode !== null) return state.orientationMobilityMode
      // Default to false - O&M category is hidden by default
      return false
    },
    aiFeatureVisibility(state, getters, rootState, rootGetters) {
      const profileId = rootGetters['contentProfile/profileId'] || ''
      return !profileId.endsWith('no-ai')
    },
    nycWebinarsVisibility(state, getters, rootState, rootGetters) {
      return rootGetters['contentProfile/profileId'] === 'new-york_no-ai'
    },
    growVisibility(state, getters, rootState, rootGetters) {
      if (state.growMode !== null) return state.growMode
      const domainFlags = rootGetters['user/domainInfo']?.flags || []
      return domainFlags.includes('grow_access') || !!rootGetters['user/hasGrowAccessTag']
    },
    hb2Visibility(state, getters, rootState, rootGetters) {
      if (state.hb2Mode !== null) return state.hb2Mode
      const domainFlags = rootGetters['user/domainInfo']?.flags || []
      return domainFlags.includes('tx_hb2_access') || !!rootGetters['user/hasHb2AccessTag']
    },
    // The opt-in/opt-out convention, taking threads-app's visibilityStore.isFeatureExcluded
    // (and its docs/feature-gating.md) as its starting point so one featureKey spells the same
    // thing in both apps. The include_ DOMAIN flag and the AI floor in
    // generateAssessmentVisibility are additions Toolkit needs and Threads has no equivalent
    // of, so the key is shared but the ladder is not identical:
    //
    //   <feature-key>           user tag     -> in, and it beats an exclude- tag
    //   exclude-<feature-key>   user tag     -> out, without touching their district
    //   exclude_<feature_key>   domain flag  -> out for the org, and it beats include_
    //   include_<feature_key>   domain flag  -> in, for a pilot district
    //
    // The mixed casing is deliberate and matches both apps: user tags are kebab-case,
    // domain flags are snake_case. NOTE that Toolkit's older gates are opt-in and use
    // space-separated tags ('grow access', see user.js) -- the two conventions now coexist
    // on purpose, so do not "fix" one to match the other.

    // The opt-IN tag is the bare feature key, as in Threads: carrying it means "this user gets
    // the feature", which is what an include- prefix used to say. Toolkit no longer reads an
    // include- user tag.

    // The opt-IN half, for a feature that is not released yet: an include_ domain flag turns
    // on a pilot district, the opt-in user tag turns on one person. Nothing consults it for
    // toolkit-pma-generation any more -- that feature is released, so
    // generateAssessmentVisibility returns true and only the exclude- half is consulted.
    // Kept because this is the shared opt-in/opt-out convention's opt-in helper, ready
    // for the next feature that pilots before it releases.
    isFeatureIncluded: (state, getters, rootState, rootGetters) => (featureKey) => {
      const tags = (rootGetters['user/userTags'] || []).map((tag) => String(tag).toLowerCase())
      if (tags.includes(featureKey)) return true

      return anyDomainHasFlag(rootState.user && rootState.user.domains, `include_${featureKey.replace(/-/g, '_')}`)
    },
    isFeatureExcluded: (state, getters, rootState, rootGetters) => (featureKey) => {
      const tags = (rootGetters['user/userTags'] || []).map((tag) => String(tag).toLowerCase())
      if (tags.includes(featureKey)) return false
      if (tags.includes(`exclude-${featureKey}`)) return true

      return anyDomainHasFlag(rootState.user && rootState.user.domains, `exclude_${featureKey.replace(/-/g, '_')}`)
    },
    generateAssessmentVisibility(state, getters, rootState, rootGetters) {
      // The Visibility Settings toggle wins outright, as it does for growMode and hb2Mode.
      // It is session-local, and the menu entry is gated to isSuperAdmin || isTestUser ||
      // isGoalbookBusinessUser (UserMenu.vue). Note isTestUser is an email check for the
      // test.k12.ca.us domain, so that is slightly wider than Goalbook staff and such a user
      // could show themselves the feature on a no-ai profile. Left above the floor for
      // consistency with the other toggles in that modal.
      if (state.generateAssessmentMode !== null) return state.generateAssessmentMode

      // Both AI gates, not either: the assessments are generated BY AI and they live in
      // Threads. A no-ai content profile must never see them, and that is a promise to the
      // district rather than a preference -- so it is the floor under everything below, and
      // only the exclusion check can subtract from what it admits.
      if (!getters.aiFeatureVisibility || !getters.threadsVisibility) return false

      // RELEASED: this is now the opt-OUT, and it is the only per-district or per-user brake
      // left. An exclude_ domain flag turns the feature off for an org, an exclude- user tag
      // for one person. Note that isFeatureExcluded lets the opt-in tag (the bare key) cancel
      // an exclude_ flag, so a single user can still be kept on in an opted-out district.
      if (getters.isFeatureExcluded(GENERATE_ASSESSMENT_FEATURE)) return false

      // Everyone the AI floor admits and nothing excluded. This getter answers "may this user
      // see the feature at all", and two narrower gates still stand between it and a Generate
      // button on a given page: generateAllowedById, the per-page authorization Toolkit
      // answers from folder role (SavedAnchorPage.vue), and the content eligibility gate in
      // util/generation_eligibility.js, which is about which goals the pipeline can serve.
      // Neither is a release gate and neither is affected by this returning true.
      return true
    },
  },
}
